import { memo, SyntheticEvent, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Radio,
  Tab,
  TabPane,
  TabProps,
} from "semantic-ui-react";

// Components
import CardVitalSign from "react-lib/apps/HISV3/PTM/CardVitalSign";
import CardPatientQueueUX from "react-lib/apps/MEH/CardPatientQueueUX";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { Table } from "react-lib/frameworks/Table";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import {
  BTN_ACTS,
  OptionsState,
  PatientQueueType,
  PickedState,
} from "react-lib/apps/MEH/sequence/PatientQueue";

// Types
import { RowInfo } from "react-table-6";
import { adToBe } from "react-lib/utils/dateUtils";

// Styles
import { STATUS_OPTIONS } from "./PaymentSlipTable";

type CardMeTriageProps = {
  //
  runSequence: any;
  setProp: any;
  onEvent: any;
  // data
  PatientQueueSequence?: PatientQueueType;
  // Common
  languageUX: "en" | "th";
  selectedEncounter?: Record<string, any>;
  selectedPatient?: Record<string, any>;
  selectedDivision: Record<string, any>;
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;
  onClose?: () => void;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus">;

const optionKeyMap: Record<string, keyof OptionsState> = {
  regularlyTakenSupplementDataList: "supplementData",
  underlyingDisease: "underlyingDisease",
};

const CardMeTriage = (props: CardMeTriageProps) => {
  const [options, setOptions] = useState<OptionsState>({
    underlyingDisease: [],
    supplementData: [],
  });

  const triageData = props.PatientQueueSequence?.triageData || {};

  useEffect(() => {
    const supplementData = props.PatientQueueSequence?.supplementData ?? [];
    const underlyingDisease = props.masterData?.underlyingDisease ?? [];

    setOptions({
      underlyingDisease: underlyingDisease.map((item: any) => ({
        key: item.id,
        value: item.id,
        text: item.name,
      })),
      supplementData: supplementData.map((item: any) => ({
        key: item.id,
        value: item.id,
        text: item.name,
      })),
    });
  }, [props.PatientQueueSequence?.supplementData, props.masterData?.underlyingDisease]);

  const handleAdditionMultiData = (_event: any, data: any) => {
    const optionKey = optionKeyMap[data.name];

    if (!optionKey) return;

    setOptions((prev) => {
      const currentOptions = prev[optionKey];

      if (currentOptions.some((item) => item.value === data.value)) {
        return prev;
      }

      return {
        ...prev,
        [optionKey]: [
          ...currentOptions,
          {
            key: data.value,
            text: data.value,
            value: data.value,
          },
        ],
      };
    });
  };

  const handleChangeData = (_event: any, data: any) => {
    const { name, value } = data;

    if (name === "preparationBeforeExam") {
      props.setProp(`PatientQueueSequence.triageData`, {
        ...props.PatientQueueSequence?.triageData,
        [name]: value,
        preparationRemark: "",
      });
    } else {
      props.setProp(`PatientQueueSequence.triageData.${name}`, value);
    }
  };

  return (
    <div className="--override-disabled --grey">
      <Form>
        <FormGroup inline>
          <FormField className={`required`} width="6">
            <label>{"อาการสำคัญ/วัตถุประสงค์การตรวจ"}</label>
          </FormField>
          <FormField width="10">
            <div style={{ width: "100%" }}>
              <Input
                fluid
                name="chiefComplaint"
                onChange={handleChangeData}
                value={triageData?.chiefComplaint || ""}
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField className={`required`} width="6">
            <label>{"โรคประจำตัว"}</label>
          </FormField>
          <FormField width="10">
            <div style={{ width: "100%" }}>
              {/* <Dropdown
                fluid
                selection
                clearable
                multiple
                search
                allowAdditions
                name="underlyingDisease"
                options={options?.underlyingDisease}
                value={triageData?.underlyingDisease || []}
                onChange={handleChangeData}
                onAddItem={handleAdditionMultiData}
              /> */}
              <Input
                fluid
                name="underlyingDisease"
                onChange={handleChangeData}
                value={triageData?.underlyingDisease || ""}
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField className={`required`} width="6">
            <label>{"ประวัติการแพ้"}</label>
          </FormField>
          <FormField width="10">
            <div style={{ width: "100%" }}>
              <div style={{ width: "100%" }}>
                <Input
                  fluid
                  name="allergy"
                  onChange={handleChangeData}
                  value={triageData?.allergy || ""}
                />
              </div>{" "}
            </div>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField className={`required`} width="6">
            <label>{"ยา/อาหารเสริมที่รับประทานประจำ"}</label>
          </FormField>
          <FormField width="10">
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                clearable
                multiple
                search
                allowAdditions
                name="regularlyTakenSupplementDataList"
                options={options.supplementData}
                value={triageData?.regularlyTakenSupplementDataList || []}
                onChange={handleChangeData}
                onAddItem={handleAdditionMultiData}
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField className={`required`} width="6">
            <label>{"การเตรียมตัวก่อนตรวจ"}</label>
          </FormField>
          <FormField>
            <div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
              <Radio
                label="งดอาหาร"
                name="preparationBeforeExam"
                value="FASTING"
                checked={triageData?.preparationBeforeExam === "FASTING"}
                onChange={handleChangeData}
              />
              <div style={{ width: "40px", margin: "0px 10px" }}>
                <Input
                  fluid
                  name="preparationRemark"
                  disabled={triageData?.preparationBeforeExam !== "FASTING"}
                  value={
                    triageData?.preparationBeforeExam === "FASTING"
                      ? triageData?.preparationRemark || ""
                      : ""
                  }
                  onChange={(e, { value }) => {
                    handleChangeData(e, {
                      value: value.replace(/\D/g, ""),
                      name: "preparationRemark",
                    });
                  }}
                />
              </div>
              <label>ชม.</label>
            </div>
          </FormField>
          <FormField>
            <div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
              <Radio
                label="ไม่งดอาหาร"
                name="preparationBeforeExam"
                value="NO_FASTING"
                checked={triageData?.preparationBeforeExam === "NO_FASTING"}
                onChange={handleChangeData}
              />
            </div>
          </FormField>
          <FormField>
            <div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
              <Radio
                label="อื่นๆ"
                name="preparationBeforeExam"
                value="OTHER"
                checked={triageData?.preparationBeforeExam === "OTHER"}
                onChange={handleChangeData}
              />
              <div style={{ width: "60px", marginLeft: "10px" }}>
                <Input
                  fluid
                  name="preparationRemark"
                  disabled={triageData?.preparationBeforeExam !== "OTHER"}
                  value={
                    triageData?.preparationBeforeExam === "OTHER"
                      ? triageData?.preparationRemark || ""
                      : ""
                  }
                  onChange={handleChangeData}
                />
              </div>{" "}
            </div>
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

export default memo(CardMeTriage);
